import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, TrendingDown } from "lucide-react";
import api, { fmtTimeCT, fmtDateCT } from "@/lib/api";
import { SectionHeading, Reveal } from "@/components/Section";

export function MoveArrow({ dir }) {
  if (dir === "up") return <TrendingUp className="ml-1 inline h-3 w-3 text-volt" data-testid="move-arrow-up" aria-label="line moved up" />;
  if (dir === "down") return <TrendingDown className="ml-1 inline h-3 w-3 text-red-400" data-testid="move-arrow-down" aria-label="line moved down" />;
  return null;
}

export function StreakChip({ streak, testid }) {
  if (!streak) return null;
  const win = streak.side === "W";
  return (
    <span
      data-testid={testid}
      title={win ? "Covering the spread lately — follow" : "Failing to cover lately — fade"}
      className={`inline-flex items-center rounded-sm border px-1.5 py-0.5 font-score text-[9px] font-bold tracking-widest ${
        win ? "border-volt/60 bg-volt/10 text-volt" : "border-liversed/60 bg-liversed/10 text-liversed"
      }`}
    >
      {streak.side}{streak.count}
    </span>
  );
}

function ATSLeaderboard() {
  const [board, setBoard] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = () => api.get("/ats/leaderboard").then(({ data }) => { if (!cancelled) setBoard(data); });
    load().catch(() => setTimeout(() => {
      if (!cancelled) load().catch(() => { if (!cancelled) setBoard({ leaderboard: [] }); });
    }, 4000));
    return () => { cancelled = true; };
  }, []);
  if (!board || board.leaderboard.length === 0) return null;
  return (
    <Reveal className="mt-6 overflow-hidden rounded-xl border border-bline bg-surface" >
      <div className="flex items-baseline justify-between gap-3 border-b border-bline bg-elevated/60 px-5 py-3">
        <p className="font-score text-[10px] tracking-[0.25em] text-volt">ATS LEADERBOARD — WHO COVERS, WHO FADES</p>
        <p className="font-score text-[9px] tracking-widest text-slate-500">LINES TRACKED SINCE {board.since}</p>
      </div>
      <div className="divide-y divide-bline" data-testid="ats-leaderboard">
        {board.leaderboard.map((r, i) => (
          <Link
            key={r.team.slug}
            to={`/team/${r.team.slug}`}
            data-testid={`ats-lb-row-${r.team.slug}`}
            className="grid grid-cols-12 items-center gap-2 px-5 py-3 transition-colors hover:bg-elevated/50"
          >
            <span className="col-span-1 font-score text-xs text-slate-500">{String(i + 1).padStart(2, "0")}</span>
            <span className="col-span-6 flex min-w-0 items-center gap-2 md:col-span-5">
              {r.team.logo && <img src={r.team.logo} alt="" className="h-5 w-5 shrink-0 object-contain" />}
              <span className="truncate text-sm font-bold text-white">{r.team.name}</span>
              <span className="hidden rounded-sm bg-bcast/15 px-1.5 py-0.5 font-score text-[9px] text-bcast sm:inline">{r.team.league}</span>
            </span>
            <span className="col-span-2 font-score text-xs font-bold text-white md:col-span-2">
              {r.ats.wins}-{r.ats.losses}{r.ats.pushes ? `-${r.ats.pushes}` : ""}
              <span className="ml-1.5 text-slate-500">ATS</span>
            </span>
            <span className="hidden font-score text-xs text-slate-400 md:col-span-2 md:block">
              {r.totals ? `O/U ${r.totals.over}-${r.totals.under}${r.totals.push ? `-${r.totals.push}` : ""}` : "—"}
            </span>
            <span className="col-span-3 flex items-center justify-end gap-2 md:col-span-2">
              <StreakChip streak={r.streak} testid={`ats-lb-streak-${r.team.slug}`} />
              <span className="font-score text-xs font-bold text-volt">{r.cover_pct != null ? `${Math.round(r.cover_pct * 100)}%` : "—"}</span>
            </span>
          </Link>
        ))}
      </div>
    </Reveal>
  );
}

export default function OddsBoard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = () => api.get("/odds").then(({ data }) => {
      if (!cancelled) { setRows(data.odds || []); setLoading(false); }
    });
    // one retry: deploys/restarts briefly drop API calls, and "No odds posted" is a lie in that window
    load().catch(() => setTimeout(() => {
      if (!cancelled) load().catch(() => { if (!cancelled) setLoading(false); });
    }, 4000));
    return () => { cancelled = true; };
  }, []);

  const fmtML = (v) => (v == null ? "—" : v > 0 ? `+${v}` : `${v}`);

  return (
    <section id="odds" className="border-y border-bline bg-surface/40 scroll-mt-24" data-testid="odds-section">
      <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-12">
        <SectionHeading index="03" eyebrow="THIS WEEK'S LINES" title="Game Odds" />
        {loading ? (
          <div className="h-48 animate-pulse rounded-xl border border-bline bg-surface" />
        ) : rows.length === 0 ? (
          <p className="rounded-xl border border-bline bg-surface p-8 text-center font-score text-sm text-slate-500" data-testid="odds-empty">
            No odds posted for current DFW matchups — check back on game day.
          </p>
        ) : (
          <Reveal className="overflow-hidden rounded-xl border border-bline bg-surface">
            <div className="hidden grid-cols-12 gap-2 border-b border-bline bg-elevated/60 px-5 py-3 font-score text-[10px] tracking-widest text-slate-500 md:grid">
              <span className="col-span-4">MATCHUP</span>
              <span className="col-span-2">START</span>
              <span className="col-span-2">SPREAD</span>
              <span className="col-span-1">O/U</span>
              <span className="col-span-2">MONEYLINE</span>
              <span className="col-span-1 text-right">BOOK</span>
            </div>
            <div className="divide-y divide-bline" data-testid="odds-table">
              {rows.slice(0, 14).map((r, i) => {
                const dfwHome = r.event.home?.name?.toLowerCase().includes(r.team.name.toLowerCase());
                const awayLogo = r.event.away?.logo || (!dfwHome ? r.team.logo : null);
                const homeLogo = r.event.home?.logo || (dfwHome ? r.team.logo : null);
                return (
                <Link
                  key={`${r.event.id}-${i}`}
                  to={`/team/${r.team.slug}`}
                  data-testid={`odds-row-${r.team.slug}-${i}`}
                  className="grid grid-cols-2 items-center gap-2 px-5 py-3.5 transition-colors hover:bg-elevated/50 md:grid-cols-12"
                >
                  <span className="col-span-2 flex items-center gap-2 md:col-span-4">
                    <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: r.team.color }} />
                    {awayLogo && <img src={awayLogo} alt="" className="h-4 w-4 shrink-0 object-contain" />}
                    <span className="truncate text-sm font-semibold text-white">
                      {r.event.away.abbr || r.event.away.name} <span className="text-slate-600">@</span> {r.event.home.abbr || r.event.home.name}
                    </span>
                    {homeLogo && <img src={homeLogo} alt="" className="h-4 w-4 shrink-0 object-contain" />}
                    <span className="rounded-sm bg-bcast/15 px-1.5 py-0.5 font-score text-[9px] text-bcast">{r.team.league}</span>
                  </span>
                  <span className="font-score text-xs text-slate-400 md:col-span-2">
                    {fmtDateCT(r.event.date, false)} {fmtTimeCT(r.event.date)}
                  </span>
                  <span className="font-score text-xs font-bold text-volt md:col-span-2">{r.event.odds?.details || "—"}<MoveArrow dir={r.event.odds?.movement?.spread} /></span>
                  <span className="font-score text-xs text-slate-300 md:col-span-1">{r.event.odds?.overUnder ?? "—"}<MoveArrow dir={r.event.odds?.movement?.total} /></span>
                  <span className="font-score text-xs text-slate-300 md:col-span-2">
                    {fmtML(r.event.odds?.moneyline_away)} / {fmtML(r.event.odds?.moneyline_home)}<MoveArrow dir={r.event.odds?.movement?.ml} />
                  </span>
                  <span className="hidden text-right font-score text-[10px] text-slate-500 md:col-span-1 md:block">{r.event.odds?.provider || "—"}</span>
                </Link>
                );
              })}
            </div>
          </Reveal>
        )}
        <ATSLeaderboard />
      </div>
    </section>
  );
}
